import React, { useCallback, useEffect, useState } from 'react';
import { SkillTag, skillTagLabel } from '../../../../services/skillFrameworkService';
import { useCourseContext } from '../../CourseContext';
import { courseRunsService } from '../../../../services/courseRunsService';

export function CourseSyllabusReview() {
  const { document, run, refreshDocument, refreshRun } = useCourseContext();
  const syllabus = document?.syllabus;
  const isLocked = !!(run as any)?.syllabus_locked;

  const [draft, setDraft] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const [showLockConfirm, setShowLockConfirm] = useState(false);

  useEffect(() => {
    if (syllabus) {
      setDraft(JSON.parse(JSON.stringify(syllabus)));
    }
  }, [syllabus]);

  const isDirty = draft && syllabus ? JSON.stringify(draft) !== JSON.stringify(syllabus) : false;

  const handleSave = useCallback(async () => {
    if (!run?.run_id || !draft) return;
    setSaving(true);
    setSaveError(null);
    setSavedMessage(null);
    try {
      await courseRunsService.updateDocument(run.run_id, { syllabus: draft });
      await refreshDocument();
      setSavedMessage('Saved');
      setTimeout(() => setSavedMessage(null), 3000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [run?.run_id, draft, refreshDocument]);

  const handleDiscard = () => {
    if (syllabus) setDraft(JSON.parse(JSON.stringify(syllabus)));
    setSaveError(null);
    setSavedMessage(null);
  };

  const handleLock = useCallback(async () => {
    if (!run?.run_id) return;
    setLocking(true);
    setLockError(null);
    try {
      await courseRunsService.lockSyllabus(run.run_id);
      await refreshRun();
      setShowLockConfirm(false);
    } catch (e: unknown) {
      setLockError(e instanceof Error ? e.message : 'Failed to lock syllabus');
    } finally {
      setLocking(false);
    }
  }, [run?.run_id, refreshRun]);

  if (!syllabus) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Generate a syllabus first before reviewing.
      </div>
    );
  }

  if (!draft) return null;

  const timeBudgetLabel = (tb: any) => tb ? `${tb.reading_minutes}m read + ${tb.exercises_minutes}m exercises` : '';

  return (
    <div className="space-y-6 pb-16">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Syllabus Review</h3>
            <p className="text-xs text-gray-500">Edit the syllabus, then lock to begin generating lessons</p>
          </div>
          <div className="flex items-center gap-3">
            {savedMessage && <span className="text-xs text-green-600">{savedMessage}</span>}
            {saveError && <span className="text-xs text-red-600">{saveError}</span>}
            <button
              type="button"
              className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              disabled={!isDirty}
              onClick={handleDiscard}
            >Discard</button>
            <button
              type="button"
              className="px-4 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: '#1a2332' }}
              disabled={saving || !isDirty}
              onClick={handleSave}
            >{saving ? 'Saving...' : 'Save Changes'}</button>
            {!isLocked && !isDirty && (
              <button
                type="button"
                className="px-4 py-1.5 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-40"
                disabled={locking || isDirty}
                onClick={() => setShowLockConfirm(true)}
              >
                Lock Syllabus
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Locked banner */}
      {isLocked && (
        <div className="bg-green-50 border border-green-200 rounded-md px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Syllabus is locked. Lesson runs have been created. Go to the Lessons Dashboard to start generating lessons.
        </div>
      )}

      {lockError && (
        <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">{lockError}</div>
      )}

      {/* Lock confirmation dialog */}
      {showLockConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setShowLockConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4 border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Lock syllabus and create lessons?</h3>
            <p className="text-sm text-gray-600">
              This will create {(draft?.lessons || []).length} lesson runs from the current syllabus.
              The syllabus will be locked for editing. You can still edit individual lessons.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setShowLockConfirm(false)}
              >Cancel</button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                disabled={locking}
                onClick={handleLock}
              >{locking ? 'Locking...' : 'Lock & Create Lessons'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Narrative arc */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Narrative Arc</label>
        <textarea
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          rows={4}
          value={draft.narrative_arc || ''}
          onChange={(e) => setDraft({ ...draft, narrative_arc: e.target.value })}
        />
      </div>

      {/* What you'll learn */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">What You'll Learn</label>
        <ListEditor
          items={draft.what_you_will_learn || []}
          onChange={(items) => setDraft({ ...draft, what_you_will_learn: items })}
          placeholder="Student-facing outcome..."
        />
      </div>

      {/* Structured goals */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Structured Goals</label>
        <ListEditor
          items={draft.structured_goals || []}
          onChange={(items) => setDraft({ ...draft, structured_goals: items })}
          placeholder="Measurable objective..."
        />
      </div>

      {/* Course glossary */}
      {draft.course_glossary && draft.course_glossary.length > 0 && (
        <details className="border border-gray-200 rounded-md">
          <summary className="px-4 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
            Course Glossary ({draft.course_glossary.length} terms)
          </summary>
          <div className="border-t divide-y divide-gray-100">
            {draft.course_glossary.map((entry: any, i: number) => (
              <div key={i} className="px-4 py-2 flex items-start gap-3">
                <input
                  className="w-40 flex-shrink-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none text-xs font-mono font-medium text-gray-900 bg-transparent py-0.5"
                  value={entry.term || ''}
                  onChange={(e) => {
                    const updated = [...draft.course_glossary];
                    updated[i] = { ...updated[i], term: e.target.value };
                    setDraft({ ...draft, course_glossary: updated });
                  }}
                />
                <input
                  className="flex-1 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none text-xs text-gray-700 bg-transparent py-0.5"
                  value={entry.canonical_short_definition || ''}
                  onChange={(e) => {
                    const updated = [...draft.course_glossary];
                    updated[i] = { ...updated[i], canonical_short_definition: e.target.value };
                    setDraft({ ...draft, course_glossary: updated });
                  }}
                />
                <span className="text-[10px] text-gray-400 flex-shrink-0 pt-1">L{entry.introduced_in_lesson}</span>
                <button
                  className="text-gray-300 hover:text-red-500 flex-shrink-0 pt-0.5"
                  onClick={() => setDraft({ ...draft, course_glossary: draft.course_glossary.filter((_: any, j: number) => j !== i) })}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Lessons header */}
      <div className="flex items-center justify-between pt-2">
        <h4 className="text-xs font-medium text-gray-500">Lessons ({(draft.lessons || []).length})</h4>
        <button
          type="button"
          className="text-xs text-blue-600 hover:text-blue-800"
          onClick={() => {
            const maxNum = Math.max(0, ...(draft.lessons || []).map((l: any) => l.lesson_number || 0));
            setDraft({
              ...draft,
              lessons: [
                ...(draft.lessons || []),
                {
                  lesson_number: maxNum + 1,
                  lesson_type: 'instructional',
                  title: 'New Lesson',
                  subtitle: '',
                  objectives: [''],
                  prerequisites: 'None',
                  exit_knowledge: '',
                  estimated_duration_minutes: document?.targeting?.approx_lesson_minutes || 7,
                  time_budget: { reading_minutes: 4, exercises_minutes: 3, exercise_count_target: 3, notes: '' },
                  skill_tags: [],
                  component_hints: [''],
                  targeting_context: '',
                },
              ],
            });
          }}
        >+ Add Lesson</button>
      </div>

      {/* Lessons */}
      <div className="space-y-2">
        {(draft.lessons || []).map((lesson: any, index: number) => {
          return (
            <LessonCard
              key={index}
              lesson={lesson}
              onChange={(updated) => {
                const lessons = [...draft.lessons];
                lessons[index] = updated;
                setDraft({ ...draft, lessons });
              }}
              onRemove={() => {
                const lessons = draft.lessons.filter((_: any, i: number) => i !== index);
                lessons.forEach((l: any, i: number) => { l.lesson_number = i + 1; });
                setDraft({ ...draft, lessons });
              }}
              timeBudgetLabel={timeBudgetLabel}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ─── Lesson Card ─── */

function LessonCard({
  lesson,
  onChange,
  onRemove,
  timeBudgetLabel,
}: {
  lesson: any;
  onChange: (updated: any) => void;
  onRemove: () => void;
  timeBudgetLabel: (tb: any) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const isTest = lesson.lesson_type === 'test';

  return (
    <div className={`border rounded-lg transition-colors ${expanded ? 'border-blue-200' : 'border-gray-200'}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <span className={`text-xs font-mono font-bold w-5 text-center ${isTest ? 'text-amber-600' : 'text-gray-400'}`}>
          {isTest ? 'T' : lesson.lesson_number}
        </span>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900">{lesson.title}</span>
          {lesson.subtitle && <span className="text-xs text-gray-400 ml-2">{lesson.subtitle}</span>}
        </div>

        <span className="text-[10px] text-gray-400 flex-shrink-0">
          {lesson.time_budget ? timeBudgetLabel(lesson.time_budget) : `~${lesson.estimated_duration_minutes || 7}m`}
        </span>

        <svg className={`w-4 h-4 text-gray-300 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="px-4 py-4 border-t border-gray-100 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FieldBlock label="Title">
              <input className={INPUT} value={lesson.title || ''} onChange={(e) => onChange({ ...lesson, title: e.target.value })} />
            </FieldBlock>
            <FieldBlock label="Subtitle">
              <input className={INPUT} value={lesson.subtitle || ''} onChange={(e) => onChange({ ...lesson, subtitle: e.target.value })} />
            </FieldBlock>
          </div>

          <FieldBlock label="Objectives">
            <ListEditor items={lesson.objectives || []} onChange={(items) => onChange({ ...lesson, objectives: items })} placeholder="Objective..." />
          </FieldBlock>

          <div className="grid grid-cols-2 gap-4">
            <FieldBlock label="Prerequisites">
              <textarea className={INPUT} rows={3} value={lesson.prerequisites || ''} onChange={(e) => onChange({ ...lesson, prerequisites: e.target.value })} />
            </FieldBlock>
            <FieldBlock label="Exit Knowledge">
              <textarea className={INPUT} rows={3} value={lesson.exit_knowledge || ''} onChange={(e) => onChange({ ...lesson, exit_knowledge: e.target.value })} />
            </FieldBlock>
          </div>

          <FieldBlock label="Component Hints">
            <ListEditor items={lesson.component_hints || []} onChange={(items) => onChange({ ...lesson, component_hints: items })} placeholder="Pedagogical intent..." />
          </FieldBlock>

          <FieldBlock label="Targeting Context">
            <textarea className={INPUT} rows={2} value={lesson.targeting_context || ''} onChange={(e) => onChange({ ...lesson, targeting_context: e.target.value })} />
          </FieldBlock>

          {lesson.time_budget && (
            <div className="grid grid-cols-4 gap-3">
              <FieldBlock label="Reading (min)">
                <input className={INPUT} type="number" value={lesson.time_budget.reading_minutes || ''} onChange={(e) => onChange({ ...lesson, time_budget: { ...lesson.time_budget, reading_minutes: Number(e.target.value) } })} />
              </FieldBlock>
              <FieldBlock label="Exercises (min)">
                <input className={INPUT} type="number" value={lesson.time_budget.exercises_minutes || ''} onChange={(e) => onChange({ ...lesson, time_budget: { ...lesson.time_budget, exercises_minutes: Number(e.target.value) } })} />
              </FieldBlock>
              <FieldBlock label="Exercise Count">
                <input className={INPUT} type="number" value={lesson.time_budget.exercise_count_target || ''} onChange={(e) => onChange({ ...lesson, time_budget: { ...lesson.time_budget, exercise_count_target: Number(e.target.value) } })} />
              </FieldBlock>
              <FieldBlock label="Total Duration (min)">
                <input className={INPUT} type="number" value={lesson.estimated_duration_minutes || ''} onChange={(e) => onChange({ ...lesson, estimated_duration_minutes: Number(e.target.value) })} />
              </FieldBlock>
            </div>
          )}

          {lesson.skill_tags !== undefined && (
            <FieldBlock label="Skill Tags">
              <div className="flex flex-wrap gap-1">
                {(lesson.skill_tags || []).length === 0 ? (
                  <span className="text-xs text-gray-400 italic">None</span>
                ) : (
                  (lesson.skill_tags || []).map((tag: SkillTag, i: number) => (
                    <span
                      key={i}
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        typeof tag === 'object' && tag?.custom
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-blue-50 text-blue-600 border-blue-100'
                      }`}
                    >
                      {skillTagLabel(tag)}
                    </span>
                  ))
                )}
              </div>
            </FieldBlock>
          )}

          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button type="button" className="text-xs text-gray-400 hover:text-red-500" onClick={onRemove}>Remove lesson</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Shared ─── */

const INPUT = 'w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500';

function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ListEditor({ items, onChange, placeholder }: { items: string[]; onChange: (items: string[]) => void; placeholder: string }) {
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <textarea
            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
            rows={1}
            value={item}
            placeholder={placeholder}
            onChange={(e) => {
              const updated = [...items];
              updated[i] = e.target.value;
              onChange(updated);
            }}
            onInput={(e) => {
              const el = e.target as HTMLTextAreaElement;
              el.style.height = 'auto';
              el.style.height = el.scrollHeight + 'px';
            }}
          />
          <button
            className="text-gray-300 hover:text-red-500 mt-1.5 flex-shrink-0"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
      <button type="button" className="text-xs text-blue-600 hover:text-blue-800" onClick={() => onChange([...items, ''])}>+ Add</button>
    </div>
  );
}
